import { NavLink } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { useProfiles } from "../profiles/ProfileContext";
import { Logo } from "../components/Logo";
import { Icon } from "./Icon";
import { HIRING_TYPES } from "../lib/jobs";
import { LOAD_POST_TYPES, LOAD_BID_TYPES } from "../lib/loads";
import { can, isStaff } from "../lib/roles";

interface NavEntry {
  to: string;
  icon: string;
  label: string;
  end?: boolean;
}

/** The app-shell left navigation. Nav groups adapt to the active profile type;
 *  on desktop it can collapse to an icon rail. */
export function Sidebar({
  onNavigate, collapsed = false, onToggleCollapse,
}: {
  onNavigate?: () => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}) {
  const { account, roles, logout } = useAuth();
  const { active } = useProfiles();
  const type = active?.type;
  const isHiring = type ? HIRING_TYPES.includes(type) : false;
  const isPoster = type ? LOAD_POST_TYPES.includes(type) : false;
  const isBidder = type ? LOAD_BID_TYPES.includes(type) : false;
  const isDriver = type === "driver";

  const activity: NavEntry[] = [
    isHiring && { to: "/jobs/mine", icon: "folder", label: "My Postings" },
    isHiring && { to: "/pipeline", icon: "applications", label: "Talent Pipeline" },
    isHiring && { to: "/crm", icon: "list", label: "CRM" },
    isHiring && { to: "/marketing", icon: "bell", label: "Marketing" },
    isHiring && { to: "/automations", icon: "settings", label: "Automations" },
    isPoster && { to: "/loads/mine", icon: "folder", label: "My Loads" },
    (isPoster || isBidder) && { to: "/dispatch", icon: "loads", label: "Dispatch" },
    type === "company" && { to: "/fleet", icon: "building", label: "Fleet" },
    type === "company" && { to: "/maintenance", icon: "settings", label: "Maintenance" },
    type === "company" && { to: "/assets", icon: "folder", label: "Assets" },
    type === "company" && { to: "/fuel", icon: "list", label: "Fuel & IFTA" },
    type === "company" && { to: "/telematics", icon: "settings", label: "Telematics & ELD" },
    (type === "company" || isDriver || isPoster) && { to: "/compliance", icon: "verify", label: "Compliance" },
    (isDriver || type === "company") && { to: "/logbook", icon: "list", label: "Log book" },
    (isDriver || type === "company") && { to: "/incidents", icon: "verify", label: "Safety incidents" },
    isDriver && { to: "/applications", icon: "applications", label: "My Applications" },
    isBidder && { to: "/bids", icon: "bids", label: "My Bids" },
  ].filter(Boolean) as NavEntry[];

  const accountItems: NavEntry[] = [
    { to: "/profiles", icon: "profile", label: "My Profiles" },
        { to: "/organizations", icon: "building", label: "Organizations" },
    { to: "/notifications", icon: "bell", label: "Notifications" },
    { to: "/referrals", icon: "connections", label: "Refer & earn" },
    { to: "/integrations", icon: "settings", label: "API keys" },
    { to: "/billing", icon: "card", label: "Billing" },
    { to: "/invoices", icon: "invoice", label: "Invoices" },
    { to: "/payments", icon: "payments", label: "Payments" },
    { to: "/disputes", icon: "flag", label: "Disputes" },
    { to: "/verify", icon: "verify", label: "Verification" },
    { to: "/settings", icon: "settings", label: "Settings" },
    { to: "/support", icon: "help", label: "Support" },
  ];

  const groups: { label: string; items: NavEntry[] }[] = [
    {
      label: "Marketplace",
      items: [
        { to: "/", icon: "dashboard", label: "Dashboard", end: true },
        { to: "/jobs", icon: "jobs", label: "Job Board" },
        { to: "/loads", icon: "loads", label: "Load Board" },
        { to: "/feed", icon: "bell", label: "Activity" },
        { to: "/communities", icon: "connections", label: "Communities" },
        { to: "/groups", icon: "messages", label: "Group Chats" },
        { to: "/marketplace", icon: "grid", label: "Marketplace" },
        { to: "/auctions", icon: "bids", label: "Auctions" },
        { to: "/analytics", icon: "analytics", label: "Analytics" },
      ],
    },
    ...(activity.length ? [{ label: "My Activity", items: activity }] : []),
    { label: "Network", items: [
      { to: "/network", icon: "search", label: "Search / Network", end: true },
      { to: "/network/company", icon: "building", label: "Companies" },
      { to: "/network/driver", icon: "profile", label: "Drivers" },
      { to: "/network/recruiter", icon: "connections", label: "Recruiters" },
      { to: "/network/broker_dispatcher", icon: "loads", label: "Brokers / Dispatchers" },
      { to: "/messages", icon: "messages", label: "Messages" },
      { to: "/connections", icon: "connections", label: "Connections" },
    ] },
    { label: "Account", items: accountItems },
  ];

  // Admin group — each link appears only if the account's roles grant it.
  const adminItems: NavEntry[] = [
    isStaff(roles) && { to: "/admin", icon: "dashboard", label: "Console" },
    can(roles, "admin_verification") && { to: "/admin/verification", icon: "admin", label: "Review Queue" },
    can(roles, "admin_users") && { to: "/admin/users", icon: "connections", label: "Users" },
    can(roles, "admin_moderation") && { to: "/admin/moderation", icon: "flag", label: "Moderation" },
    can(roles, "admin_moderation") && { to: "/admin/disputes", icon: "flag", label: "Disputes" },
    can(roles, "admin_support") && { to: "/admin/support", icon: "help", label: "Support" },
    can(roles, "admin_feedback") && { to: "/admin/feedback", icon: "idea", label: "Feedback" },
    can(roles, "admin_users") && { to: "/admin/audit", icon: "security", label: "Audit log" },
    can(roles, "admin_users") && { to: "/admin/flags", icon: "settings", label: "Feature flags" },
  ].filter(Boolean) as NavEntry[];
  if (adminItems.length) groups.push({ label: "Admin", items: adminItems });

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 rounded-lg py-2 text-sm font-semibold transition-colors ${collapsed ? "justify-center px-0" : "px-3"} ${
      isActive ? "bg-accent text-accent-ink" : "text-muted hover:bg-surface-2 hover:text-ink"
    }`;

  return (
    <aside className={`flex h-full flex-col border-r border-line bg-surface transition-[width] duration-150 ${collapsed ? "w-16" : "w-60"}`}>
      <div className={`flex items-center gap-2.5 py-4 ${collapsed ? "justify-center px-2" : "px-4"}`}>
        <Logo size={30} />
        {!collapsed && (
          <span className="font-display text-[15px] font-black uppercase tracking-tight">
            <span className="text-ink">Recruit</span><span className="text-accent">Trucker</span>
          </span>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto px-2 pb-4">
        {groups.map((g) => (
          <div key={g.label} className="mb-1">
            {collapsed ? (
              <div className="mx-2 my-2 border-t border-line/60" />
            ) : (
              <p className="px-3 pb-1 pt-4 text-[10px] font-bold uppercase tracking-widest text-muted/70">{g.label}</p>
            )}
            {g.items.map((it) => (
              <NavLink key={it.to} to={it.to} end={it.end} onClick={onNavigate} title={collapsed ? it.label : undefined} className={linkClass}>
                <Icon name={it.icon} />
                {!collapsed && <span>{it.label}</span>}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      <div className="grid gap-2 border-t border-line p-3">
        {collapsed ? (
          <span className="mx-auto grid h-9 w-9 place-items-center rounded-full bg-accent/15 font-display text-sm font-black text-accent-strong" title={active?.display_name ?? account?.email ?? ""}>
            {(active?.display_name ?? account?.email ?? "?").slice(0, 1).toUpperCase()}
          </span>
        ) : (
          <div className="flex items-center gap-2.5">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-accent/15 font-display text-sm font-black text-accent-strong">
              {(active?.display_name ?? account?.email ?? "?").slice(0, 1).toUpperCase()}
            </span>
            <div className="grid min-w-0">
              <span className="truncate text-sm font-bold">{active?.display_name ?? account?.email ?? "—"}</span>
              <span className="font-mono text-[11px] text-muted">
                {account?.identity_verified ? <span className="text-verified">VERIFIED ✓</span> : "unverified"}
              </span>
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={() => void logout()}
          aria-label="Sign out"
          title="Sign out"
          className={`flex items-center gap-1.5 text-xs font-bold text-muted transition-colors hover:text-danger ${collapsed ? "justify-center" : ""}`}
        >
          <Icon name="logout" className="h-4 w-4" />
          {!collapsed && "Sign out"}
        </button>

        {onToggleCollapse && (
          <button
            type="button"
            onClick={onToggleCollapse}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={collapsed ? "Expand" : "Collapse"}
            className={`flex items-center gap-1.5 border-t border-line pt-2 text-xs font-bold text-muted transition-colors hover:text-ink ${collapsed ? "justify-center" : ""}`}
          >
            <Icon name="collapse" className={`h-4 w-4 transition-transform ${collapsed ? "rotate-180" : ""}`} />
            {!collapsed && "Collapse"}
          </button>
        )}
      </div>
    </aside>
  );
}
